import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { T3Wordmark, tokens } from '@thesis3/design-system';
import { useAuth } from '../auth/AuthProvider';
import { login as startLogin } from '../auth/oidc';
import { useT } from '../i18n';

const DRAWER_WIDTH = 220;

function UserMenu() {
  const { user, login, logout, roles } = useAuth();
  const t = useT();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (!user) {
    return (
      <Button variant="contained" color="secondary" onClick={login}>
        {t('Sign in')}
      </Button>
    );
  }

  const displayName = user.profile.name ?? user.profile.preferred_username ?? user.profile.sub;

  return (
    <>
      <Button
        color="inherit"
        onClick={(event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
        aria-haspopup="menu"
        sx={{ color: tokens.color.cream, fontWeight: 700 }}
      >
        {displayName}
      </Button>
      <Menu open={anchorEl !== null} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2">{displayName}</Typography>
          <Typography variant="caption" color="text.secondary">
            {roles.length > 0 ? roles.join(' · ') : t('no roles')}
          </Typography>
        </Box>
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            logout();
          }}
        >
          {t('Sign out')}
        </MenuItem>
      </Menu>
    </>
  );
}

/** Branded shell chrome: ink app bar, wordmark, user menu, left nav.
 * Anonymous visitors never see the chrome: any signed-out visit forwards
 * straight to the Keycloak sign-in screen with only a bare splash meanwhile. */
export function Layout() {
  const { pathname } = useLocation();
  const { user, loading, login } = useAuth();
  const t = useT();
  const attempted = useRef(false);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && !user && !attempted.current) {
      attempted.current = true;
      // Call the oidc layer directly so a failed redirect SURFACES instead of
      // being swallowed — a silent failure strands the user on this splash.
      startLogin().catch((e: unknown) => {
        setAuthError(e instanceof Error ? e.message : String(e));
        attempted.current = false;
      });
    }
  }, [loading, user]);

  if (loading || !user) {
    return (
      <Box className="flex min-h-screen flex-col items-center justify-center gap-3">
        <Typography color="text.secondary">
          {loading ? t('Checking session…') : t('Redirecting to sign in…')}
        </Typography>
        {authError ? (
          <>
            <Typography color="error" variant="body2">
              {t('Sign-in redirect failed:')} {authError}
            </Typography>
            <Button variant="contained" color="secondary" onClick={login}>
              {t('Try again')}
            </Button>
          </>
        ) : null}
      </Box>
    );
  }

  return (
    <Box className="flex min-h-screen">
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ gap: 2 }}>
          <NavLink
            to="/workbench"
            aria-label={t('Underwriting workbench')}
            className="flex items-center"
          >
            <T3Wordmark dark height={26} />
          </NavLink>
          <Box sx={{ flexGrow: 1 }} />
          <UserMenu />
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
      >
        <Toolbar />
        <List component="nav" aria-label={t('Platform navigation')}>
          <ListItemButton
            component={NavLink}
            to="/workbench"
            selected={pathname.startsWith('/workbench')}
          >
            <ListItemText primary={t('Workbench')} />
          </ListItemButton>
          <ListItemButton
            component={NavLink}
            to="/tenants"
            selected={pathname.startsWith('/tenants')}
          >
            <ListItemText primary={t('Tenants')} />
          </ListItemButton>
        </List>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 3, backgroundColor: 'background.default' }}>
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}
